import React, { useState } from 'react';
import {
  ChevronDown,
  Eye,
  FileText,
  Image as ImageIcon,
  Pencil,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import FormInput from 'shared/components/forms/FormInput';
import FormAutocomplete from 'shared/components/forms/FormAutocomplete';
import { cn } from 'shared/lib/utils';
import type { FileMeta } from 'features/onboarding/types/onboarding.types';

export interface ReviewItemEdit {
  type: 'text' | 'number' | 'autocomplete';
  value: string;
  onChange: (value: string) => void;
  error?: string;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
}

export interface ReviewItem {
  label: string;
  value: React.ReactNode;
  /** Validation key shown when final submission validation fails. */
  error?: string;
  /** Optional file to render as a clickable preview thumbnail. */
  preview?: FileMeta;
  /** When set, the value cell renders an editable control instead of static text. */
  edit?: ReviewItemEdit;
}

/** Tri-state status for a section. */
export type SectionStatus = 'complete' | 'incomplete' | 'invalid';

interface ReviewSectionProps {
  title: string;
  items: ReviewItem[];
  onEdit?: () => void;
  icon?: LucideIcon;
  defaultOpen?: boolean;
  /** Per-section validation result. When omitted, falls back to filled-count. */
  status?: SectionStatus;
  /** Anchor id so submit-time validation can scroll to the offending section. */
  anchorId?: string;
  /** Click handler when the user clicks a preview thumbnail. */
  onPreview?: (file: FileMeta, item: ReviewItem) => void;
  /** When true, hide the Edit button (e.g. after submission). */
  readOnly?: boolean;
}

const isEmptyValue = (value: React.ReactNode): boolean =>
  value === null || value === undefined || value === '';

const ReviewSection: React.FC<ReviewSectionProps> = ({
  title,
  items,
  onEdit,
  icon: Icon,
  defaultOpen = true,
  status,
  anchorId,
  onPreview,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);

  const filled = items.filter((i) => {
    if (i.edit) return i.edit.value.trim().length > 0;
    return !isEmptyValue(i.value);
  }).length;
  const total = items.length;
  const isComplete = status ? status === 'complete' : filled === total && total > 0;
  const isInvalid = status === 'invalid';
  const hasEditable = items.some((i) => i.edit) && !readOnly;

  return (
    <section
      id={anchorId}
      className={cn(
        'rounded-2xl border bg-white overflow-hidden scroll-mt-24',
        isInvalid ? 'border-red-200' : 'border-gray-200',
      )}
    >
      <header className="flex items-center gap-3 p-4 sm:p-5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3 flex-1 min-w-0 text-start"
          aria-expanded={open}
        >
          {Icon && (
            <span className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0">
              <Icon size={18} />
            </span>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
              {title}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('review.fieldsFilled', { filled, total, defaultValue: `${filled} / ${total}` })}
            </p>
          </div>
          <span
            className={cn(
              'hidden sm:inline-flex text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border',
              isInvalid
                ? 'bg-red-50 text-red-700 border-red-100'
                : isComplete
                  ? 'bg-green-50 text-green-700 border-green-100'
                  : 'bg-amber-50 text-amber-700 border-amber-100',
            )}
          >
            {isInvalid
              ? t('review.statusInvalid', { defaultValue: 'Needs fixing' })
              : isComplete
                ? t('review.statusComplete', { defaultValue: 'Complete' })
                : t('review.statusIncomplete', { defaultValue: 'Incomplete' })}
          </span>
          <ChevronDown
            size={18}
            className={cn(
              'text-gray-400 transition-transform flex-shrink-0',
              open && 'rotate-180',
            )}
          />
        </button>
        {onEdit && !readOnly && !hasEditable && (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 min-h-[36px]"
          >
            <Pencil size={12} />
            <span className="hidden sm:inline">{t('common.edit')}</span>
          </button>
        )}
      </header>

      {open && (
        <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-gray-100">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            {items.map((item) => {
              const empty = isEmptyValue(item.value);
              const file = item.preview;
              const isImg = file?.type.startsWith('image/');
              const isPdf = file?.type === 'application/pdf';
              const edit = !readOnly ? item.edit : undefined;
              const error = edit?.error ?? item.error;
              const errorMessage = error
                ? t(`validation.${error}`, { defaultValue: error })
                : undefined;

              return (
                <div
                  key={item.label}
                  data-error={error ? 'true' : undefined}
                  tabIndex={error && !edit ? -1 : undefined}
                  className={cn(
                    'min-w-0 rounded-lg',
                    edit?.type === 'autocomplete' && 'sm:col-span-2',
                    error && !edit && 'border border-red-200 bg-red-50/60 p-2',
                  )}
                >
                  {edit ? (
                    edit.type === 'autocomplete' ? (
                      <FormAutocomplete
                        label={item.label}
                        options={edit.options ?? []}
                        value={edit.value}
                        onChange={edit.onChange}
                        error={edit.error}
                        disabled={edit.disabled}
                        placeholder={edit.placeholder}
                        required
                      />
                    ) : (
                      <FormInput
                        label={item.label}
                        value={edit.value}
                        onChange={(e) => edit.onChange(e.target.value)}
                        error={edit.error}
                        inputMode={edit.type === 'number' ? 'numeric' : undefined}
                        placeholder={edit.placeholder}
                        required
                      />
                    )
                  ) : (
                    <>
                      <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        {item.label}
                      </dt>
                      <dd className="text-sm text-gray-800 mt-0.5 break-words">
                        {empty ? (
                          <span className="text-gray-300">—</span>
                        ) : file && onPreview ? (
                          <button
                            type="button"
                            onClick={() => onPreview(file, item)}
                            className="group inline-flex items-center gap-2.5 max-w-full text-start min-h-[44px] py-1 pe-2 rounded-lg hover:bg-gray-50 transition-colors"
                            title={t('review.viewFile', { defaultValue: 'View file' }) as string}
                          >
                            <span className="flex-shrink-0">
                              {isImg ? (
                                <img
                                  src={file.url}
                                  alt={file.name}
                                  className="w-10 h-10 rounded-lg object-cover border border-gray-200 group-hover:border-gray-400 transition-colors"
                                />
                              ) : isPdf ? (
                                <span className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center border border-gray-200">
                                  <FileText size={18} />
                                </span>
                              ) : (
                                <span className="w-10 h-10 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center border border-gray-200">
                                  <ImageIcon size={18} />
                                </span>
                              )}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-gray-800 group-hover:text-gray-900 transition-colors">
                                {String(item.value)}
                              </span>
                              <span className="block text-[11px] text-gray-500 group-hover:text-gray-700 transition-colors inline-flex items-center gap-1">
                                <Eye size={11} />
                                {t('review.viewFile', { defaultValue: 'View file' })}
                              </span>
                            </span>
                          </button>
                        ) : (
                          item.value
                        )}
                      </dd>
                      {errorMessage && (
                        <p className="mt-1 text-xs font-medium text-red-600">
                          {errorMessage}
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </dl>
        </div>
      )}
    </section>
  );
};

export default ReviewSection;
